import { IMeetingConfig, IOperationResultNoValue, MeetingConfigViewModel, MeetingDateRange } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ILocationState } from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { invalidateSchedulerConfigsQuery, useSchedulerConfigDeleteMutation } from '../../../../../queries';
import { Button } from '../../../../components-v2/Button';
import { INotificationServiceComponentProps } from '../../../LocalNotificationObserver/WithNotificationService';
import { MultiContainerHeader } from '../../../MultiContainerHeader';
import { AutomaticReminders } from './components/AutomaticReminders';
import { Availability } from './components/Availability';
import { BasicInfo } from './components/BasicInfo';
import { SchedulingLimits } from './components/SchedulingLimits';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any>, INotificationServiceComponentProps<any> {
	meeting: MeetingConfigViewModel;
	onClose(): void;
	styles?: StyleDeclarationValue[];
}

export type IMeetingConfigModalLocationState = ILocationState<MeetingConfigViewModel, IMeetingConfig>;

const MeetingConfigSFC: React.FC<IProps> = ({ meeting, onClose }) => {
	const logger = useEventLogging();
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	const [ccEmailError, setCcEmailError] = useState('');

	const meetingRange = meeting?.meetingDateRadio;
	const handleError = (err: IOperationResultNoValue) => {
		logger.logApiError('ScheduleMeetingDelete-Error', err);

		errorMessages.pushApiError(err);
	};
	const handleOnClose = () => {
		const userId = meeting?.forUser?.id ?? userSession.user.id;
		invalidateSchedulerConfigsQuery({ userId });
		onClose();
	};
	const deleteMeetingMutation = useSchedulerConfigDeleteMutation({
		onError: handleError,
		onSuccess: handleOnClose,
	});

	useEffect(() => {
		meeting.setAsEditing()?.catch((err: IOperationResultNoValue) => {
			logger.logApiError('MeetingConfigSetAsEditing-Error', err);
			// do not need to show user that this failed...
		});
		meeting.getAutomationChoices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onCancelMeetingConfig = useCallback(() => {
		if (meeting.isPublished) {
			meeting.discardChanges()?.then(handleOnClose)?.catch(handleError);
		} else {
			const userId = meeting?.forUser?.id ?? userSession.user.id;
			deleteMeetingMutation.mutate({
				meetingId: meeting.id,
				userId,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting, onClose]);

	const onSaveMeetingConfig = () => {
		if (meetingRange === MeetingDateRange.DateRange) {
			const from = moment(meeting.dateRange.start);

			const to = moment(meeting.dateRange.end);
			if (to > moment(maxDate)) {
				errorMessages.push({
					messages: ['To date cannot be more than 2 years in the future'],
				});
				return;
			}
			if (from >= to) {
				errorMessages.push({
					messages: ['From date cannot be greater than to date'],
				});
				return;
			}
		}

		meeting
			.commitChanges()
			?.then(() => {
				invalidateSchedulerConfigsQuery({ userId: meeting.forUser?.id ?? userSession.user.id });
				onClose();
			})
			?.catch((err: IOperationResultNoValue) => {
				logger.logApiError('MeetingConfigCommitingChanges-Error', err);

				errorMessages.pushApiError(err);
			});
	};

	const maxDate = new Date();
	maxDate.setDate(maxDate.getDate() + MeetingConfigViewModel.MAX_DATE_RANGE_DAYS);

	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader
				fullscreenHeader={`${meeting?.isPublished ? 'Edit' : 'New'} Meeting${
					meeting?.forUser ? ` for ${getDisplayName(meeting?.forUser)}` : ''
				}`}
				onFullscreenRequestBack={onCancelMeetingConfig}
				onFullscreenRequestClose={onCancelMeetingConfig}
			/>

			<div className={css(styleSheet.wrapper)}>
				<BasicInfo meeting={meeting} onEmailError={setCcEmailError} />
				<Availability meeting={meeting} />
			</div>
			<div className={css(styleSheet.cardBottomContainer)}>
				<div className={css(styleSheet.card)}>
					<SchedulingLimits meeting={meeting} />
				</div>
			</div>
			<AutomaticReminders meeting={meeting} />
			<div className={css(styleSheet.footer)}>
				<Button
					disabled={meeting?.isBusy || meeting?.isSaving || ccEmailError.length > 0}
					variant='primary'
					isLoading={meeting?.isSaving}
					onClick={onSaveMeetingConfig}
				>
					Save
				</Button>
				<Button disabled={meeting?.isBusy || meeting?.isSaving} variant='secondary' onClick={onCancelMeetingConfig}>
					Cancel
				</Button>
			</div>
		</div>
	);
};

export const MeetingConfigModal = observer(MeetingConfigSFC);
